// Overlay permanent : le PLAN DU COLLÈGE, en bas à droite. Affiche TOUS les agents (élèves,
// profs) en points, dans toutes les salles à la fois — un point par agent, mis à jour ~1/s par
// le flux `world-pulse` du serveur (positions seulement, 0 token). Le joueur est mis en évidence.
import 'phaser'
import { SceneName } from './enums/SceneNames'
import GlobalInfo from '../GlobalInfo'
import { CAMPUS_CELLS, CAMPUS_W, CAMPUS_H, toMinimapCell } from './minimapLayout'
import { SHIRT_HEX, type PulseAgent } from '../net/protocol'

const CELL = 3, MARGIN = 10, PAD = 5, TITLE_H = 12

// Per-agent glide between the ~1s world-pulse updates: lerp from the last position to the new one.
type AnimEntry = { fromCol: number; fromRow: number; toCol: number; toRow: number; t0: number }

export default class MinimapScene extends Phaser.Scene {
    private panel!: Phaser.GameObjects.Rectangle
    private title!: Phaser.GameObjects.Text
    private holidayChip!: Phaser.GameObjects.Text
    private bg!: Phaser.GameObjects.Graphics
    private dots!: Phaser.GameObjects.Graphics
    private ox = 0
    private oy = 0
    private latest: PulseAgent[] = []
    private anim = new Map<string, AnimEntry>()
    private static readonly PULSE_MS = 1000
    private shown = true

    constructor() { super(SceneName.Minimap) }

    create(): void {
        this.panel = this.add.rectangle(0, 0, CAMPUS_W * CELL + PAD * 2, CAMPUS_H * CELL + PAD * 2 + TITLE_H, 0x10151f, 0.74)
            .setOrigin(0).setStrokeStyle(1, 0xffffff, 0.18).setScrollFactor(0).setDepth(1900)
        this.title = this.add.text(0, 0, 'Plan du collège  ·  M', { fontFamily: 'sans-serif', fontSize: '8px', color: '#cbd5e0' })
            .setScrollFactor(0).setResolution(3).setDepth(1901)
        // Élèves "hors campus" (carte vacances) : un compteur dans la barre de titre plutôt que des
        // points entassés sur le bâtiment (la carte vacances n'a pas d'emplacement sur le plan).
        this.holidayChip = this.add.text(0, 0, '', { fontFamily: 'sans-serif', fontSize: '8px', color: '#f6e05e' })
            .setOrigin(1, 0).setScrollFactor(0).setResolution(3).setDepth(1901).setVisible(false)
        this.bg = this.add.graphics().setScrollFactor(0).setDepth(1901)
        this.dots = this.add.graphics().setScrollFactor(0).setDepth(1902)

        this.layout()
        this.scale.on('resize', this.layout, this)
        GlobalInfo.on('server-world-pulse', this.onPulse, this)
        GlobalInfo.on('toggle-minimap', this.toggle, this)
        this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
            GlobalInfo.off('server-world-pulse', this.onPulse, this)
            GlobalInfo.off('toggle-minimap', this.toggle, this)
            this.scale.off('resize', this.layout, this)
        })
    }

    private layout = (): void => {
        const pw = CAMPUS_W * CELL + PAD * 2
        const ph = CAMPUS_H * CELL + PAD * 2 + TITLE_H
        this.ox = this.scale.width - pw - MARGIN
        this.oy = this.scale.height - ph - MARGIN
        this.panel.setPosition(this.ox, this.oy)
        this.title.setPosition(this.ox + PAD, this.oy + 3)
        this.holidayChip.setPosition(this.ox + pw - PAD, this.oy + 3)
        this.drawBackground()
        this.drawDots()
    }

    private gx(): number { return this.ox + PAD }
    private gy(): number { return this.oy + PAD + TITLE_H }

    private drawBackground(): void {
        this.bg.clear()
        if (!this.shown) return
        const x0 = this.gx(), y0 = this.gy()
        for (let r = 0; r < CAMPUS_H; r++) {
            const row = CAMPUS_CELLS[r]
            for (let c = 0; c < CAMPUS_W; c++) {
                this.bg.fillStyle(row[c], 1)
                this.bg.fillRect(x0 + c * CELL, y0 + r * CELL, CELL, CELL)
            }
        }
    }

    // On each pulse, retarget every dot: its NEW `from` is wherever it currently sits (mid-glide), its
    // `to` is the freshly reported cell. update() then lerps from→to over PULSE_MS for smooth motion.
    private onPulse = (p: { agents: PulseAgent[] }): void => {
        const now = this.time.now
        const next = new Map<string, AnimEntry>()
        for (const a of p.agents) {
            if (a.location === 'holiday' || a.location.startsWith('room:')) continue // home/off-site: not on the campus plan
            const m = toMinimapCell(a.location, a.tile)
            const prev = this.anim.get(a.id)
            const cur = prev ? this.lerpCell(prev, now) : { col: m.col, row: m.row }
            next.set(a.id, { fromCol: cur.col, fromRow: cur.row, toCol: m.col, toRow: m.row, t0: now })
        }
        this.anim = next
        this.latest = p.agents
        // Holiday agents live on a map that isn't on the campus plan → count them in the title bar
        // instead of clamping their dots onto the building footprint.
        const onHoliday = p.agents.filter(a => a.location === 'holiday').length
        this.holidayChip.setText(onHoliday ? `🏖️ ${onHoliday}` : '').setVisible(this.shown && onHoliday > 0)
    }

    // Redraw every frame at the interpolated positions (the scene is a launched overlay, so update runs).
    update(): void { this.drawDots() }

    private lerpCell(an: AnimEntry, now: number): { col: number; row: number } {
        const t = Math.max(0, Math.min(1, (now - an.t0) / MinimapScene.PULSE_MS))
        return { col: an.fromCol + (an.toCol - an.fromCol) * t, row: an.fromRow + (an.toRow - an.fromRow) * t }
    }

    private drawDots(): void {
        this.dots.clear()
        if (!this.shown) return
        const now = this.time.now
        const x0 = this.gx(), y0 = this.gy()
        for (const a of this.latest) {
            if (a.location === 'holiday' || a.location.startsWith('room:')) continue // home/off-site: not on the campus plan
            const an = this.anim.get(a.id)
            const c = an ? this.lerpCell(an, now) : toMinimapCell(a.location, a.tile)
            const px = x0 + c.col * CELL + CELL / 2, py = y0 + c.row * CELL + CELL / 2
            if (a.kind === 'player') {
                this.dots.fillStyle(0xffffff, 1); this.dots.fillCircle(px, py, 2.4)
                this.dots.lineStyle(1.2, 0x2b6cb0, 1); this.dots.strokeCircle(px, py, 3.6)
            } else {
                const color = a.kind === 'staff' ? 0xf6ad55 : (a.shirt ? parseInt(SHIRT_HEX[a.shirt], 16) : 0x63b3ed)
                this.dots.fillStyle(color, a.named ? 1 : 0.8)
                this.dots.fillCircle(px, py, a.named ? 2.1 : 1.6)
            }
        }
    }

    private toggle = (): void => {
        this.shown = !this.shown
        this.panel.setVisible(this.shown); this.title.setVisible(this.shown)
        this.drawBackground(); this.drawDots()
    }
}
